import React, { useEffect, useState, useRef, useCallback } from 'react'
import { FiMusic, FiExternalLink, FiAlertTriangle, FiPlay, FiPause, FiSkipForward, FiSkipBack, FiList } from 'react-icons/fi'

// Constants for localStorage keys
const DEVICE_ID_KEY = 'spotify_device_id'
const WEBVIEW_DEVICE_ID_KEY = 'spotify_webview_device_id'

// Helper to detect if we're in a webview
const isWebView = () => {
  // User agent detection for common webview signatures
  const ua = navigator.userAgent.toLowerCase()
  
  // iOS WKWebView detection
  const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(ua)
  
  // Android WebView detection
  const isAndroidWebView = ua.includes('wv') || 
    (ua.includes('android') && ua.includes('webview'))
  
  // Check for query parameter that can be passed when embedding
  const urlParams = new URLSearchParams(window.location.search)
  const isWebViewParam = urlParams.get('webview') === 'true'
  
  // Check for specific window properties that might indicate embedding
  const hasWindowParent = window !== window.parent
  
  return isIOSWebView || isAndroidWebView || isWebViewParam || hasWindowParent
}

// Get the appropriate device ID key based on environment
const getDeviceIdKey = () => {
  return isWebView() ? WEBVIEW_DEVICE_ID_KEY : DEVICE_ID_KEY
}

interface PlayerProps {
  playlistId: string | null
  accessToken: string | null
  deviceId: string | null
  onDeviceId: (deviceId: string) => void
}

interface TrackItem {
  id: string
  name: string
  artists: { name: string }[]
  duration_ms: number
  uri: string
  album: {
    name: string
    images: { url: string }[]
  }
}

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady: () => void
    Spotify: any
    spotifyPlayer?: any
  }
}

// Store player state in session storage to persist between component mounts
// We use a global variable to store player instance reference
let globalPlayerInstance: any = null;
let globalCurrentTrack: any = null;
let globalIsPlaying: boolean = false;

const Player: React.FC<PlayerProps> = ({ 
  playlistId, 
  accessToken, 
  deviceId,
  onDeviceId
}) => {
  const [player, setPlayer] = useState<any>(globalPlayerInstance)
  const [playerReady, setPlayerReady] = useState(!!globalPlayerInstance)
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [currentTrack, setCurrentTrack] = useState<any>(globalCurrentTrack)
  const [isPlaying, setIsPlaying] = useState(globalIsPlaying)
  const [showHints, setShowHints] = useState(false)
  const sdkScriptRef = useRef<HTMLScriptElement | null>(null)
  const [isSdkLoaded, setIsSdkLoaded] = useState(!!window.Spotify)
  const [useFallback, setUseFallback] = useState(false)
  const [playlistTracks, setPlaylistTracks] = useState<TrackItem[]>([])
  const [showTrackList, setShowTrackList] = useState(false)
  const [isLoadingTracks, setIsLoadingTracks] = useState(false)
  const [deviceValidated, setDeviceValidated] = useState(false)
  const [deviceCheckInProgress, setDeviceCheckInProgress] = useState(false)
  const playerRef = useRef<HTMLDivElement>(null)
  const retryAttemptsRef = useRef(0)
  
  // Update global state when local state changes
  useEffect(() => {
    if (player) {
      globalPlayerInstance = player;
    }
    if (currentTrack) {
      globalCurrentTrack = currentTrack;
    }
    globalIsPlaying = isPlaying;
  }, [player, currentTrack, isPlaying]);

  // Restore player state from localStorage on component mount
  useEffect(() => {
    // Try to load track info from localStorage even if we don't have player yet
    if (!currentTrack) {
      try {
        const savedPlayerState = localStorage.getItem('spotify_player_state');
        if (savedPlayerState) {
          const state = JSON.parse(savedPlayerState);
          
          // If saved playlist matches current playlist, try to restore track info
          if (state && state.playlistId === playlistId) {
            // We'll set isPlaying only if we have a player instance that can actually play
            if (player && state.isPlaying) {
              setIsPlaying(state.isPlaying);
            }
            
            // If we don't have track info, fetch it
            if (!currentTrack && state.trackId && accessToken) {
              fetch(`https://api.spotify.com/v1/tracks/${state.trackId}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
              })
              .then(response => {
                if (!response.ok) throw new Error('Failed to fetch track');
                return response.json();
              })
              .then(trackData => {
                if (trackData) {
                  console.log('Restored track info from API:', trackData);
                  setCurrentTrack(trackData);
                  globalCurrentTrack = trackData;
                }
                
                // Also fetch playlist tracks
                fetchPlaylistTracks();
              })
              .catch(err => {
                console.error('Failed to restore track info:', err);
                // Just fetch playlist tracks
                fetchPlaylistTracks();
              });
            } else {
              // Fetch playlist tracks anyway
              fetchPlaylistTracks();
            }
          } else {
            // If playlist changed, just fetch new playlist tracks
            fetchPlaylistTracks();
          }
        } else {
          // No saved state, fetch playlist tracks
          fetchPlaylistTracks();
        }
      } catch (error) {
        console.error('Error restoring player state:', error);
        // Just fetch playlist tracks
        fetchPlaylistTracks();
      }
    }
  }, [playlistId, accessToken]);

  // Only start playback when we have a new playlist ID 
  // and no current track or the track is from another playlist
  useEffect(() => {
    if (playlistId && accessToken && deviceId && playerReady) {
      // Always fetch the tracks for the current playlist
      fetchPlaylistTracks();
      
      // Check if this is a new playlist or we don't have a current track
      const savedState = localStorage.getItem('spotify_player_state');
      const parsedState = savedState ? JSON.parse(savedState) : {};
      const shouldStartNewPlayback = (!currentTrack || parsedState.playlistId !== playlistId) && !playerError;
      
      if (shouldStartNewPlayback) {
        console.log('Starting playback for new playlist:', playlistId);
        
        // Delay playback slightly to ensure Spotify API is ready
        const timer = setTimeout(() => {
          console.log('Starting playback with device ID:', deviceId);
          startPlayback();
        }, 1500);
        return () => clearTimeout(timer);
      } else {
        console.log('Not starting playback - using existing player state');
      }
    }
  }, [playlistId, accessToken, deviceId, playerReady, currentTrack, playerError]);
  
  // Load Spotify Web Playback SDK
  useEffect(() => {
    if (!accessToken) return
    
    // Check if SDK is already loaded
    if (window.Spotify) {
      setIsSdkLoaded(true)
      console.log('Spotify SDK already loaded, isWebView:', isWebView())
      return
    }
    
    if (!sdkScriptRef.current) {
      console.log('Loading Spotify SDK, isWebView:', isWebView())
      const script = document.createElement('script')
      script.src = 'https://sdk.scdn.co/spotify-player.js'
      script.async = true
      document.body.appendChild(script)
      sdkScriptRef.current = script
      
      window.onSpotifyWebPlaybackSDKReady = () => {
        console.log('Spotify Web Playback SDK loaded, isWebView:', isWebView())
        setIsSdkLoaded(true)
      }
    }
    
    // Only disconnect on final unmount, not view changes
    return () => {
      // Do nothing during normal component remounting
    }
  }, [accessToken])
  
  // Persist player state in local storage to maintain across view changes
  useEffect(() => {
    if (playlistId && currentTrack) {
      localStorage.setItem('spotify_player_state', JSON.stringify({
        playlistId,
        trackId: currentTrack.id,
        isPlaying
      }))
    }
  }, [playlistId, currentTrack, isPlaying])
  
  // Add an effect to update device ID when player is ready or reused
  useEffect(() => {
    if (player && playerReady && !deviceId) {
      // Get the device ID from the player if we don't have one
      player.getDeviceId()
        .then((id: string) => {
          console.log('Retrieved device ID from player:', id)
          const deviceIdKey = getDeviceIdKey()
          localStorage.setItem(deviceIdKey, id)
          onDeviceId(id)
        })
        .catch((err: any) => {
          console.error('Failed to get device ID from player:', err)
        })
    }
  }, [player, playerReady, deviceId, onDeviceId])
  
  // Always load device ID from localStorage on component mount, before connecting
  useEffect(() => {
    const deviceIdKey = getDeviceIdKey()
    const savedDeviceId = localStorage.getItem(deviceIdKey)
    if (savedDeviceId && !deviceId) {
      console.log(`Restoring saved device ID from ${deviceIdKey}:`, savedDeviceId)
      onDeviceId(savedDeviceId)
    }
  }, [deviceId, onDeviceId])
  
  // Prevent player disconnection when component remounts
  useEffect(() => {
    // Attempt to store player reference globally to maintain state
    if (player && !window.spotifyPlayer) {
      window.spotifyPlayer = player
    }
    
    // Set up the beforeunload event listener
    const handleBeforeUnload = () => {
      // Ensure we keep the device ID in localStorage before unloading
      if (deviceId) {
        const deviceIdKey = getDeviceIdKey()
        localStorage.setItem(deviceIdKey, deviceId)
      }
      
      if (player) {
        player.disconnect()
      }
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    // Clean up only if navigating away completely, not on remount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [player, deviceId])
  
  // Stabilize initialize player effect to prevent reinitialization
  useEffect(() => {
    if (!isSdkLoaded || !accessToken) return;
    
    let cleanupInitialized = false;
    console.log('Initializing player, isWebView:', isWebView(), 'deviceIdKey:', getDeviceIdKey());
    
    // If we already have a global player instance, use that
    if (globalPlayerInstance) {
      console.log('Reusing existing Spotify player instance from global state');
      setPlayer(globalPlayerInstance);
      setPlayerReady(true);
      
      // Make sure we have a device ID
      if (!deviceId && globalPlayerInstance) {
        globalPlayerInstance.getDeviceId()
          .then((id: string) => {
            console.log('Retrieved device ID from existing player:', id);
            localStorage.setItem(getDeviceIdKey(), id);
            onDeviceId(id);
          })
          .catch((err: any) => {
            console.error('Failed to get device ID from existing player:', err);
          });
      }
      return;
    }
    
    // Try to restore a previous device ID if available
    const deviceIdKey = getDeviceIdKey();
    const savedDeviceId = localStorage.getItem(deviceIdKey);
    console.log(`Checking for saved device ID with key ${deviceIdKey}:`, savedDeviceId);
    
    if (savedDeviceId && !deviceId) {
      console.log('Restoring saved device ID:', savedDeviceId);
      onDeviceId(savedDeviceId);
    }
    
    // Initialize the player only once with delay for better reliability
    const initTimer = setTimeout(() => {
      if (!player && !cleanupInitialized) {
        connect()
          .then(success => {
            if (!success) {
              console.warn('Failed to connect to Spotify Player, using fallback');
              setUseFallback(true);
            }
          })
          .catch(err => {
            console.error('Error connecting to player:', err);
            setUseFallback(true);
          });
      }
    }, 500);
    
    return () => {
      cleanupInitialized = true;
      clearTimeout(initTimer);
    };
    // Intentionally omit deviceId from deps to prevent reinitialization
  }, [isSdkLoaded, accessToken, player]);

  // Fix the device validation effect to handle errors better
  useEffect(() => {
    if (!accessToken || !deviceId || deviceValidated || deviceCheckInProgress) return;
    
    let validationInProgress = false;
    
    const validateDevice = async () => {
      if (validationInProgress) return;
      
      validationInProgress = true;
      setDeviceCheckInProgress(true);
      
      try {
        console.log(`Validating device ID: ${deviceId} using key: ${getDeviceIdKey()}`);
        
        // First check if the device ID is still valid
        const response = await fetch('https://api.spotify.com/v1/me/player/devices', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            // Token is invalid, we'll let the useSpotify hook handle refresh
            console.error('Token is invalid when validating device');
            setDeviceCheckInProgress(false);
            validationInProgress = false;
            return;
          }
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        const devices = data.devices || [];
        const deviceExists = devices.some((d: any) => d.id === deviceId);
        
        console.log('Device validation check:', { 
          deviceId, 
          deviceExists, 
          availableDevices: devices.map((d: any) => d.id),
          isWebView: isWebView(),
          deviceIdKey: getDeviceIdKey()
        });
        
        if (!deviceExists && retryAttemptsRef.current < 3) {
          // If device isn't found but we have retry attempts left, try to create a new one
          console.log(`Device ID not found in available devices (attempt ${retryAttemptsRef.current + 1})`);
          retryAttemptsRef.current++;
          
          // Try to reconnect with slight delay
          setTimeout(() => {
            if (player) {
              player.connect().then(() => {
                console.log('Reconnected player after device validation failure');
                validationInProgress = false;
                setDeviceCheckInProgress(false);
              }).catch((err: Error) => {
                console.error('Failed to reconnect player:', err);
                validationInProgress = false;
                setDeviceCheckInProgress(false);
              });
            } else {
              // If no player, try to initialize one
              connect().catch(err => {
                console.error('Failed to initialize player:', err);
                validationInProgress = false;
                setDeviceCheckInProgress(false);
              });
            }
          }, 1000);
        } else if (!deviceExists) {
          // If we've exhausted retries, clear the device ID
          console.log('Device ID not found after multiple attempts, clearing...');
          // Keep the ID in localStorage but clear from state
          // This will allow us to try again on next mount
          onDeviceId('');
          setPlayerError('Device not found. Reconnecting...');
          
          // Reset player state
          setPlayer(null);
          setPlayerReady(false);
          retryAttemptsRef.current = 0;
          validationInProgress = false;
          setDeviceCheckInProgress(false);
        } else {
          console.log('Device validated successfully');
          setDeviceValidated(true);
          setPlayerError(null);
          validationInProgress = false;
          setDeviceCheckInProgress(false);
        }
      } catch (error) {
        console.error('Error validating device:', error);
        validationInProgress = false;
        setDeviceCheckInProgress(false);
      }
    };
    
    validateDevice();
    
    // Cleanup function to prevent memory leaks
    return () => {
      validationInProgress = true; // Prevent any ongoing validation from completing
    };
  }, [accessToken, deviceId, onDeviceId, deviceValidated, deviceCheckInProgress, player]);

  // Add back the connect function
  const connect = async (): Promise<boolean> => {
    if (!window.Spotify || !accessToken) {
      console.error('Spotify Web Playback SDK not available')
      setPlayerError('Spotify player unavailable')
      return false
    }

    try {
      console.log('Initializing Spotify player, isWebView:', isWebView(), 'deviceIdKey:', getDeviceIdKey())
      setPlayerError(null)
      
      // Try up to 3 times to initialize the player (up from previous attempts)
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const spotifyPlayer = new window.Spotify.Player({
            name: isWebView() ? 'Mood Music Web Player (WebView)' : 'Mood Music Web Player',
            getOAuthToken: (cb: (token: string) => void) => { cb(accessToken) },
            volume: 0.5
          })
          
          // Add event listeners
          spotifyPlayer.addListener('ready', ({ device_id }: { device_id: string }) => {
            console.log('Spotify player ready with device ID:', device_id, 'isWebView:', isWebView())
            const deviceIdKey = getDeviceIdKey()
            console.log(`Storing device ID with key ${deviceIdKey}:`, device_id)
            localStorage.setItem(deviceIdKey, device_id)
            onDeviceId(device_id)
            setPlayerReady(true)
            setDeviceValidated(true)
            
            // Store player instance both in state and globally
            setPlayer(spotifyPlayer)
            globalPlayerInstance = spotifyPlayer
            window.spotifyPlayer = spotifyPlayer
            retryAttemptsRef.current = 0
          })
          
          spotifyPlayer.addListener('not_ready', ({ device_id }: { device_id: string }) => {
            console.log('Device ID is not ready for playback', device_id)
            setPlayerReady(false)
          })
          
          spotifyPlayer.addListener('player_state_changed', (state: any) => {
            if (!state) return
            
            const track = state.track_window.current_track
            setCurrentTrack(track)
            setIsPlaying(!state.paused)
          })
          
          spotifyPlayer.addListener('initialization_error', ({ message }: { message: string }) => {
            console.error('Initialization error:', message)
            setPlayerError(`Failed to initialize player: ${message}`)
            return false
          })
          
          spotifyPlayer.addListener('authentication_error', ({ message }: { message: string }) => {
            console.error('Authentication error:', message)
            setPlayerError(`Authentication failed: ${message}`)
            return false
          })
          
          spotifyPlayer.addListener('account_error', ({ message }: { message: string }) => {
            console.error('Account error:', message)
            setPlayerError('Premium account required for playback in browser')
            setShowHints(true)
            setUseFallback(true)
            return false
          })
          
          spotifyPlayer.addListener('playback_error', ({ message }: { message: string }) => {
            console.error('Playback error event:', message)
            
            // If we get a device not found error, clear the device ID
            if (message.includes('device not found') || message.includes('device_not_found')) {
              console.log('Device not found in playback error, clearing device ID...');
              // We don't remove from localStorage to allow retrying on next mount
              onDeviceId('');
              setDeviceValidated(false);
              
              console.log('Attempting to reconnect device...')
              setTimeout(() => {
                spotifyPlayer.disconnect().then(() => {
                  return spotifyPlayer.connect()
                }).catch((err: Error) => {
                  console.error('Reconnection failed:', err)
                  setUseFallback(true)
                })
              }, 1000)
            }
          })
          
          // Connect
          const connected = await spotifyPlayer.connect()
          if (connected) {
            console.log('Connected to Spotify player successfully')
            return true
          } else {
            console.warn(`Player connection attempt ${attempt} failed, retrying...`)
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        } catch (err) {
          console.error(`Player initialization error (attempt ${attempt}):`, err)
          if (attempt === 3) throw err
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
      
      throw new Error('Failed to connect after multiple attempts')
    } catch (err) {
      console.error('Failed to connect to Spotify player:', err)
      setPlayerError('Failed to connect to Spotify. Please try refreshing the page.')
      return false
    }
  }

  // Add back the fetchPlaylistTracks function
  const fetchPlaylistTracks = async () => {
    if (!playlistId || !accessToken) return
    
    setIsLoadingTracks(true)
    
    try {
      const response = await fetch(`https://api.spotify.com/v1/playlists/${playlistId}/tracks?limit=50`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tracks: ${response.status}`)
      }
      
      const data = await response.json()
      
      if (data.items && Array.isArray(data.items)) {
        const tracks = data.items.map((item: any) => item.track).filter(Boolean)
        setPlaylistTracks(tracks)
      }
    } catch (error) {
      console.error('Error fetching playlist tracks:', error)
      setPlayerError(`Failed to load tracks: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoadingTracks(false)
    }
  }

  // Refactor startPlayback to avoid unnecessary API calls
  const startPlayback = useCallback(async () => {
    if (!accessToken || !deviceId || !playlistId) {
      console.error('Missing required data for playback');
      return;
    }
    
    // If we already have a track playing from this playlist, don't start again
    const savedState = localStorage.getItem('spotify_player_state');
    const parsedState = savedState ? JSON.parse(savedState) : {};
    if (parsedState.playlistId === playlistId && isPlaying && currentTrack) {
      console.log('Already playing this playlist, skipping startPlayback');
      return;
    }
    
    try {
      // First check if the device ID is still valid - important when cookies are cleared
      const deviceResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!deviceResponse.ok) {
        throw new Error(`Failed to check devices: ${deviceResponse.status}`);
      }
      
      const deviceData = await deviceResponse.json();
      const deviceExists = deviceData.devices.some((d: any) => d.id === deviceId);
      
      if (!deviceExists) {
        console.log('Device not found when starting playback, attempting to reconnect...');
        // Don't clear from localStorage, just from state to try again later
        onDeviceId('');
        setDeviceValidated(false);
        return;
      }
      
      // Get current player state
      const playerStateResponse = await fetch('https://api.spotify.com/v1/me/player', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      // If no player is active, we need to activate our device first
      if (playerStateResponse.status === 204) {
        console.log('No active player, activating device first...');
        await fetch('https://api.spotify.com/v1/me/player', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            device_ids: [deviceId],
            play: false
          })
        });
        
        // Wait for device activation to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Now start playback on the playlist
      console.log(`Starting playback for playlist ${playlistId} on device ${deviceId}`);
      const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          context_uri: `spotify:playlist:${playlistId}`
        })
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // Clear the deviceId since it's not found
          localStorage.removeItem(getDeviceIdKey());
          onDeviceId('');
          setPlayerError('Device not found. Please refresh to reconnect.');
        } else if (response.status === 403) {
          setPlayerError('Spotify Premium is required for playback control.');
          setShowHints(true);
          setUseFallback(true);
        } else {
          const errorData = await response.json();
          setPlayerError(`Playback error: ${errorData.error?.message || response.statusText}`);
        }
      } else {
        setPlayerError(null);
        // Reset retry counter on success
        retryAttemptsRef.current = 0;
        // Mark as playing
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error starting playback:', error);
      setPlayerError('Failed to start playback. Please try again.');
      
      // Implement retry logic with increasing delay
      if (retryAttemptsRef.current < 3) {
        const delay = 1000 * Math.pow(2, retryAttemptsRef.current);
        console.log(`Retrying playback in ${delay}ms (attempt ${retryAttemptsRef.current + 1}/3)`);
        
        setTimeout(() => {
          retryAttemptsRef.current++;
          startPlayback();
        }, delay);
      }
    }
  }, [accessToken, deviceId, playlistId, onDeviceId, isPlaying, currentTrack]);
  
  const handlePlayPause = () => {
    if (!accessToken || !deviceId) {
      console.error('Missing auth token or device ID for playback control');
      return;
    }
    
    // If SDK player is available, try to use it
    if (player) {
      console.log('Toggling playback with SDK player');
      try {
        player.togglePlay()
          .then(() => {
            console.log('Playback toggled successfully');
            // Update state optimistically - the player_state_changed event will update it properly
            setIsPlaying(!isPlaying);
          })
          .catch((error: any) => {
            console.error('Error toggling playback with SDK:', error);
            // Try direct API call as fallback
            useDirectApiForPlayback();
          });
      } catch (error) {
        console.error('Exception in togglePlay:', error);
        useDirectApiForPlayback();
      }
    } else {
      // No SDK player available, use direct API
      useDirectApiForPlayback();
    }
    
    // Helper function for using direct API as fallback
    function useDirectApiForPlayback() {
      if (!accessToken || !deviceId) return;
      
      console.log('Using direct API for playback control');
      const endpoint = isPlaying ? 
        `https://api.spotify.com/v1/me/player/pause?device_id=${deviceId}` : 
        `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`;
      
      const options = {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      };
      
      // If starting playback and we have a playlist ID, include the context
      const body = !isPlaying && playlistId ? 
        JSON.stringify({
          context_uri: `spotify:playlist:${playlistId}`,
        }) : undefined;
      
      if (body) {
        Object.assign(options, { 
          body,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json'
          }
        });
      }
      
      fetch(endpoint, options)
        .then(response => {
          if (!response.ok) {
            if (response.status === 404) {
              throw new Error('Device not found');
            } else if (response.status === 403) {
              throw new Error('Premium required');
            }
            throw new Error(`API error: ${response.status}`);
          }
          
          // Update state if successful
          console.log('Direct API playback control successful');
          setIsPlaying(!isPlaying);
        })
        .catch((err: Error) => {
          console.error('Failed direct API call:', err);
          setPlayerError(`Playback control failed: ${err.message}`);
          if (err.message.includes('Premium') || err.message.includes('403')) {
            setUseFallback(true);
            setShowHints(true);
          }
        });
    }
  };
  
  const handleNextTrack = () => {
    if (!accessToken || !deviceId) {
      console.error('Missing auth token or device ID for next track');
      return;
    }
    
    if (player) {
      try {
        player.nextTrack()
          .then(() => {
            console.log('Skipped to next track');
          })
          .catch((error: any) => {
            console.error('Error skipping to next track with SDK:', error);
            useDirectApiForNextTrack();
          });
      } catch (error) {
        console.error('Exception in nextTrack:', error);
        useDirectApiForNextTrack();
      }
    } else {
      useDirectApiForNextTrack();
    }
    
    function useDirectApiForNextTrack() {
      if (!accessToken || !deviceId) return;
      
      console.log('Using direct API for next track');
      fetch(`https://api.spotify.com/v1/me/player/next?device_id=${deviceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
      .then(response => {
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Device not found');
          } else if (response.status === 403) {
            throw new Error('Premium required');
          }
          throw new Error(`API error: ${response.status}`);
        }
        
        console.log('Direct API next track successful');
        
        // Optionally fetch current state after changing track
        setTimeout(() => {
          fetchCurrentPlayback();
        }, 300);
      })
      .catch((err: Error) => {
        console.error('Failed direct API call for next track:', err);
        setPlayerError(`Playback control failed: ${err.message}`);
        if (err.message.includes('Premium') || err.message.includes('403')) {
          setUseFallback(true);
          setShowHints(true);
        }
      });
    }
  };
  
  const handlePreviousTrack = () => {
    if (!accessToken || !deviceId) {
      console.error('Missing auth token or device ID for previous track');
      return;
    }
    
    if (player) {
      try {
        player.previousTrack()
          .then(() => {
            console.log('Skipped to previous track');
          })
          .catch((error: any) => {
            console.error('Error skipping to previous track with SDK:', error);
            useDirectApiForPreviousTrack();
          });
      } catch (error) {
        console.error('Exception in previousTrack:', error);
        useDirectApiForPreviousTrack();
      }
    } else {
      useDirectApiForPreviousTrack();
    }
    
    function useDirectApiForPreviousTrack() {
      if (!accessToken || !deviceId) return;
      
      console.log('Using direct API for previous track');
      fetch(`https://api.spotify.com/v1/me/player/previous?device_id=${deviceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
      .then(response => {
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Device not found');
          } else if (response.status === 403) {
            throw new Error('Premium required');
          }
          throw new Error(`API error: ${response.status}`);
        }
        
        console.log('Direct API previous track successful');
        
        // Optionally fetch current state after changing track
        setTimeout(() => {
          fetchCurrentPlayback();
        }, 300);
      })
      .catch((err: Error) => {
        console.error('Failed direct API call for previous track:', err);
        setPlayerError(`Playback control failed: ${err.message}`);
        if (err.message.includes('Premium') || err.message.includes('403')) {
          setUseFallback(true);
          setShowHints(true);
        }
      });
    }
  };
  
  const playSpecificTrack = async (trackUri: string) => {
    if (!accessToken || !deviceId) return
    
    try {
      if (useFallback) return
      
      const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          context_uri: `spotify:playlist:${playlistId}`,
          offset: { uri: trackUri }
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        console.error('Error playing specific track:', errorData || response.statusText)
        setPlayerError(`Failed to play track. Try refreshing.`)
        
        if (response.status === 403) {
          setUseFallback(true)
          setShowHints(true)
        }
      }
    } catch (error) {
      console.error('Error playing specific track:', error)
      setPlayerError(`Playback error: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setUseFallback(true)
    }
  }
  
  const formatDuration = (ms: number): string => {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // Implement a retry mechanism for failed playbacks
  const retryPlayback = useCallback((attemptCount = 0, maxAttempts = 3) => {
    if (attemptCount >= maxAttempts) {
      console.error(`Exceeded maximum retry attempts (${maxAttempts}), using fallback`)
      setUseFallback(true)
      return
    }
    
    console.log(`Retrying playback (attempt ${attemptCount + 1}/${maxAttempts})`)
    
    // Short delay before retry, increasing with each attempt
    setTimeout(() => {
      startPlayback().catch((error) => {
        console.error(`Retry attempt ${attemptCount + 1} failed:`, error)
        retryPlayback(attemptCount + 1, maxAttempts)
      })
    }, 1000 * (attemptCount + 1))
  }, [])

  // When component mounts, check if we need to update state from other globals
  useEffect(() => {
    // If we have global state but local state doesn't match, update local state
    if (globalPlayerInstance && player !== globalPlayerInstance) {
      console.log('Updating local player from global instance');
      setPlayer(globalPlayerInstance);
      setPlayerReady(true);
    }
    
    if (globalCurrentTrack && currentTrack !== globalCurrentTrack) {
      console.log('Updating current track from global state');
      setCurrentTrack(globalCurrentTrack);
    }
    
    if (globalIsPlaying !== isPlaying) {
      console.log('Updating playing state from global');
      setIsPlaying(globalIsPlaying);
    }
  }, []);

  // When page unloads fully (not just component changes), clean up player
  useEffect(() => {
    const handleUnload = () => {
      if (player) {
        console.log('Page unloading, disconnecting player');
        player.disconnect();
      }
    };
    
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [player]);

  // Helper to fetch current playback state
  const fetchCurrentPlayback = useCallback(() => {
    if (!accessToken) return;
    
    fetch('https://api.spotify.com/v1/me/player/currently-playing', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    .then(response => {
      if (response.status === 204) {
        console.log('No content returned from currently playing endpoint');
        return null;
      }
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data && data.item) {
        console.log('Retrieved current playback state:', data);
        setCurrentTrack(data.item);
        globalCurrentTrack = data.item;
        setIsPlaying(data.is_playing);
        globalIsPlaying = data.is_playing;
      }
    })
    .catch((err: Error) => {
      console.error('Error fetching current playback:', err);
    });
  }, [accessToken]);

  // Device detection and connection error handling
  useEffect(() => {
    if (playerReady && deviceId && !isPlaying && playlistId) {
      // If device is found but playback fails, try to recover by reconnecting
      console.log('Attempting to recover from device not found error');
      
      // Wait a bit for the device registration to complete
      const timer = setTimeout(() => {
        try {
          // First try to get the player status
          fetch('https://api.spotify.com/v1/me/player', {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          })
          .then(response => {
            if (response.status === 204) {
              console.log('No active player found, transferring playback...');
              // Transfer playback to our device
              return fetch('https://api.spotify.com/v1/me/player', {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  device_ids: [deviceId],
                  play: false
                })
              });
            }
            return response;
          })
          .then(() => {
            console.log('Attempting playback after device recovery');
            startPlayback();
          })
          .catch((err: Error) => {
            console.error('Recovery attempt failed:', err);
          });
        } catch (err) {
          console.error('Error during recovery:', err);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [playerError]);

  // Add a window storage event listener to detect when cookies are cleared
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      const deviceIdKey = getDeviceIdKey()
      
      if (e.key === deviceIdKey && e.newValue && !deviceId) {
        console.log('Device ID updated in another tab, syncing:', e.newValue)
        onDeviceId(e.newValue)
      }
      
      if (e.key === deviceIdKey && !e.newValue && deviceId) {
        console.log('Device ID removed in another tab, clearing local state')
        onDeviceId('')
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [player, onDeviceId])

  // Add a cleanup function to preserve device ID
  useEffect(() => {
    // Add a cleanup handler to save device ID on unmount
    return () => {
      if (deviceId) {
        const deviceIdKey = getDeviceIdKey()
        localStorage.setItem(deviceIdKey, deviceId)
      }
    }
  }, [deviceId])

  if (!accessToken) {
    return (
      <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg flex flex-col items-center justify-center min-h-[200px]">
        <FiMusic className="text-spotify-text-gray text-4xl mb-3" />
        <p className="text-spotify-text-gray text-center">
          You need to log in to play music
        </p>
      </div>
    )
  }

  if (!playlistId) {
    return (
      <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg flex flex-col items-center justify-center min-h-[200px]">
        <FiMusic className="text-spotify-text-gray text-4xl mb-3" />
        <p className="text-spotify-text-gray text-center">
          Select a playlist to start playing
        </p>
      </div>
    )
  }

  // Render fallback embedded player if SDK player fails or user doesn't have premium
  if (useFallback) {
  return (
      <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg flex flex-col h-full">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold mb-2">Spotify Playlist</h3>
          {playerError && (
            <div className="text-spotify-error text-sm flex items-center justify-center mb-3">
              <FiAlertTriangle className="mr-1" />
              <span>{playerError}</span>
            </div>
          )}
        </div>
        
        <div className="flex-grow overflow-hidden rounded-lg">
      <iframe
            title="Spotify Embedded Player"
            src={`https://open.spotify.com/embed/playlist/${playlistId}`}
        width="100%"
        height="100%"
            style={{ minHeight: '300px' }}
        frameBorder="0"
            allowTransparency={true} 
            allow="encrypted-media"
        className="rounded-lg"
          ></iframe>
        </div>
        
        {/* Add link to open in Spotify app for better experience on all devices */}
        <div className="mt-4 text-center">
          <a 
            href={`spotify:playlist:${playlistId}`}
            className="inline-block px-4 py-2 bg-spotify-green text-white rounded-full"
          >
            Open in Spotify
          </a>
        </div>
      </div>
    )
  }

  return (
    <div 
      ref={playerRef}
      className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg flex flex-col"
      style={{ 
        height: showTrackList ? 'auto' : 'fit-content',
        maxHeight: showTrackList ? '550px' : 'none'
      }}
    >
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold">Now Playing</h2>
        <div className="flex items-center space-x-2">
          {!playerReady && !useFallback && (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-spotify-green mr-1"></div>
          )}
          <button 
            onClick={() => setShowTrackList(!showTrackList)}
            className="p-2 rounded-full hover:bg-spotify-input transition-colors text-spotify-text-gray"
            title={showTrackList ? "Show Player" : "Show Track List"}
          >
            {showTrackList ? <FiMusic /> : <FiList />}
          </button>
        </div>
      </div>
      
      {playerError && (
        <div className="text-spotify-error text-sm flex items-center mb-3 p-2 bg-spotify-error bg-opacity-10 rounded-lg">
          <FiAlertTriangle className="mr-2 flex-shrink-0" />
          <span>{playerError}</span>
          <button 
            onClick={() => setShowHints(!showHints)} 
            className="ml-auto text-xs underline"
          >
            {showHints ? 'Hide help' : 'Show help'}
          </button>
        </div>
      )}
      
      {showTrackList ? (
        <div 
          className="overflow-y-auto" 
          style={{ 
            maxHeight: '400px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          <style>
            {`
              div::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          
          <h3 className="font-medium mb-3">Tracks in this playlist</h3>
          {isLoadingTracks ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-spotify-green"></div>
            </div>
          ) : playlistTracks.length === 0 ? (
            <div className="text-center py-6 text-spotify-text-gray">
              <p>No tracks found in this playlist</p>
            </div>
          ) : (
            <ul className="divide-y divide-spotify-border">
              {playlistTracks.map((track, index) => (
                <li 
                  key={track.id + index} 
                  className={`py-2 px-1 flex items-center hover:bg-spotify-input rounded-md cursor-pointer ${
                    currentTrack && currentTrack.id === track.id ? 'bg-spotify-input' : ''
                  }`}
                  onClick={() => playSpecificTrack(track.uri)}
                >
                  <div className="mr-3 w-8 text-center text-spotify-text-gray">
                    {currentTrack && currentTrack.id === track.id ? (
                      <div className="w-4 h-4 bg-spotify-green animate-pulse rounded-full mx-auto"></div>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="flex-grow mr-2 truncate">
                    <div className={`truncate ${currentTrack && currentTrack.id === track.id ? 'text-spotify-green font-medium' : ''}`}>
                      {track.name}
                    </div>
                    <div className="text-xs text-spotify-text-gray truncate">
                      {track.artists.map(a => a.name).join(', ')}
                    </div>
                  </div>
                  <div className="text-xs text-spotify-text-gray">
                    {formatDuration(track.duration_ms)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          {currentTrack ? (
            <div className="flex flex-col items-center text-center">
              <div className="w-full max-w-[250px] aspect-square mb-4 overflow-hidden rounded-lg shadow-lg">
                {currentTrack.album.images?.[0]?.url ? (
                  <img 
                    src={currentTrack.album.images[0].url} 
                    alt={currentTrack.album.name}
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full bg-spotify-dark flex items-center justify-center">
                    <FiMusic className="text-4xl text-spotify-text-gray" />
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-1 truncate max-w-full">
                  {currentTrack.name}
                </h3>
                
                <p className="text-spotify-text-gray mb-4 truncate max-w-full">
                  {currentTrack.artists.map((a: any) => a.name).join(', ')}
                </p>
                
                <div className="flex justify-center items-center space-x-4 mb-4">
                  <button 
                    onClick={handlePreviousTrack}
                    className="p-2 rounded-full hover:bg-spotify-input transition-colors"
                  >
                    <FiSkipBack />
                  </button>
                  <button 
                    onClick={handlePlayPause}
                    className="p-4 bg-spotify-green rounded-full hover:bg-opacity-90 transition-colors text-white"
                  >
                    {isPlaying ? <FiPause /> : <FiPlay />}
                  </button>
                  <button 
                    onClick={handleNextTrack}
                    className="p-2 rounded-full hover:bg-spotify-input transition-colors"
                  >
                    <FiSkipForward />
                  </button>
                </div>
                
                <a 
                  href={`https://open.spotify.com/track/${currentTrack.id}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center text-spotify-green hover:text-spotify-green-bright transition-colors text-sm"
                >
                  <span>Open in Spotify</span>
                  <FiExternalLink className="ml-1" />
                </a>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center">
              <div className="animate-pulse flex flex-col items-center justify-center">
                <div className="w-48 h-48 bg-spotify-input rounded-lg mb-4"></div>
                <div className="h-5 bg-spotify-input rounded w-36 mb-2"></div>
                <div className="h-4 bg-spotify-input rounded w-24"></div>
              </div>
            </div>
          )}
        </>
      )}
      
      {showHints && (
        <div className="mt-4 text-sm text-spotify-text-gray border-t border-spotify-border pt-4">
          <p className="mb-2">
            <strong>Need help?</strong>
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Spotify Premium is required for playback in browser</li>
            <li>Make sure you have the Spotify app open on your device</li>
            <li>Try using the "Open in Spotify" link instead</li>
          </ul>
        </div>
      )}
    </div>
  )
}

export default Player 